import { Autocomplete, type Item } from '@ads/components-react';
import { type FocusEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsOverflowing } from '@/shared/lib/hooks/useIsOverflowing';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

import { LOOKUP_MIN_CHARS, type LookupOption } from './lookupSearch';

export interface LookupAutocompleteViewProps {
  readonly label: string;
  readonly placeholder: string;
  /** ID selecionado como string (`''` quando nada). */
  readonly value: string;
  /** Rótulo da seleção corrente. */
  readonly selectedLabel: string;
  readonly options: readonly LookupOption[];
  readonly isQuerySuccess: boolean;
  readonly search: string;
  readonly onSearchChange: (term: string) => void;
  readonly onSelect: (id: string, label: string) => void;
  readonly onClear: () => void;
  readonly hasError?: boolean;
  /**
   * Classe extra na lista de opções, para elevá-la acima de um dialog.
   *
   * A lista é portada para o `body` como irmã do dialog, então quem monta o
   * autocomplete dentro de um modal precisa dizer em que camada ela entra — o
   * `z-[60]` padrão cobre o uso em página, não o empilhamento de cada modal.
   */
  readonly optionListClassName?: string | undefined;
}

/**
 * Base presentacional dos autocompletes de lookup. Envolve o `Autocomplete` do
 * ADS com busca server-side (mensagens de estado via `getAutocompleteSearchMessage`).
 * Não conhece a origem dos dados — recebe `options`/`isQuerySuccess` de um hook
 * de dropdown específico consumido pelo wrapper.
 *
 * Componente genérico e compartilhado: qualquer feature pode montar um wrapper
 * (ex.: `CompanyAutocomplete`, `AuthorAutocomplete`) sobre esta base.
 *
 * @example
 * ```tsx
 * <LookupAutocompleteView
 *   label={t('author')}
 *   placeholder={t('authorPlaceholder')}
 *   value={value !== null ? String(value) : ''}
 *   selectedLabel={selectedLabel}
 *   options={options}
 *   isQuerySuccess={isQuerySuccess}
 *   search={search}
 *   onSearchChange={setSearch}
 *   onSelect={handleSelect}
 *   onClear={handleClear}
 * />
 * ```
 */
export function LookupAutocompleteView({
  label,
  placeholder,
  value,
  selectedLabel,
  options,
  isQuerySuccess,
  search,
  onSearchChange,
  onSelect,
  onClear,
  hasError = false,
  optionListClassName,
}: LookupAutocompleteViewProps) {
  const { t: tCommon } = useTranslation('common');
  const { ref: inputRef, isOverflowing } = useIsOverflowing<HTMLInputElement>();

  // O `Autocomplete` do ADS mantém o texto exibido em estado interno (`query`) e
  // só o reseta a partir de `selectedItem.label` quando `selectedItem.value !==
  // null`. Ao limpar, `value` vira `null` e esse reset interno é pulado — o texto
  // antigo permaneceria visível. Remontar o componente (via `key`) reinicializa o
  // `query` a partir do `selectedItem.label` já vazio, limpando o campo de fato.
  const [clearNonce, setClearNonce] = useState(0);
  const handleClear = () => {
    onClear();
    setClearNonce(n => n + 1);
  };

  /**
   * Alinha o termo de busca ao que o campo realmente exibe.
   *
   * O texto visível é estado **interno** do ADS: fechar o campo sem escolher nada
   * o esvazia, mas o `search` do pai continuava com o termo digitado — e reabrir o
   * campo mostrava a lista daquela busca, com o input vazio. Ao focar, o input é a
   * verdade: vazio nele significa busca zerada aqui.
   */
  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (value !== '' || search === '') return;
    if (event.currentTarget.value.trim() === '') onSearchChange('');
  };

  // Só oferece opções quando o texto atual justifica uma busca. O `Autocomplete`
  // do ADS guarda a lista em estado interno (`filteredList`) e a filtra pelo texto
  // digitado — com o campo vazio, `''` casa com tudo, então a lista da busca
  // anterior continuava aparecendo ao reabrir o campo (o hook de busca ainda
  // segura o `data` do termo antigo durante o debounce). Zerando a lista abaixo do
  // mínimo, o campo vazio só pode exibir a mensagem "digite ao menos N caracteres".
  const hasSearchableTerm = search.trim().length >= LOOKUP_MIN_CHARS;
  const items = hasSearchableTerm
    ? options.map((o): Item<string> => ({ label: o.label, value: String(o.id) }))
    : [];
  // O `Autocomplete` do ADS força `query := selectedItem.label` a cada render
  // (efeito guardado por `value !== null`). Passar `selectedItem` só quando há
  // seleção mantém esse efeito DESLIGADO durante a busca: o texto digitado
  // aparece normalmente e a lista não é refiltrada pelo `query` (mantendo as
  // opções clicáveis). Quando há seleção, o rótulo é exibido no campo.
  const selectedItem: Item<string> = { label: selectedLabel, value };

  const notFoundMessage = getAutocompleteSearchMessage(
    search.length,
    isQuerySuccess,
    items.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <Autocomplete<string>
      key={clearNonce}
      ref={inputRef}
      label={label}
      placeholder={placeholder}
      size="md"
      hasError={hasError}
      list={items}
      custom={{
        input: '!w-full truncate',
        optionList: optionListClassName ?? 'z-[60]',
        option: 'truncate',
      }}
      {...(value ? { selectedItem } : {})}
      {...(notFoundMessage !== undefined ? { notFoundMessage } : {})}
      {...(isOverflowing && selectedLabel ? { title: selectedLabel } : {})}
      onFocus={handleFocus}
      onChange={e => onSearchChange(e.target.value)}
      handleChange={item => onSelect(item.value ?? '', item.label ?? '')}
      handleClearQuery={handleClear}
    />
  );
}
